import * as React from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { PromoModal } from '@cian/frontend-newbuilding-promo-modal-widget';

import { Responsive } from '@cian/ui-kit';
import { usePreparedPromo } from './hooks';
import { trackCianMortgageLinkClick, trackCreditFullPriceClick } from './tracking';
import { trackPromoLinkClickAction } from '../../actions/analytics';
import { closePromoDetails } from '../../actions/promoDetails';
import { CianMortgageButton } from '../../components/CianMortgageButton';
import { PROMO_ID_QUERYSTRING } from '../../constants/promoIdQuery';
import { usePromoDetailsConfig } from '../../hooks/promoDetails';
import { selectCurrentPromoId, selectPromoDetails } from '../../selectors/promoDetails';
import { IPromoOffer } from '../../types/promoOffersSelection';
import { TThunkDispatch } from '../../types/redux/thunk';
import { PromoOfferCardContext } from '../../utils/promoOfferCardContext';
import { isPathContainsPromoId } from '../../utils/topPromoUrl/isPathContainsPromoId';
import { getLocationPath } from '../../utils/url';
import { removeSearchParam } from '../../utils/url/removeSearchParam';
import { CallButton } from '../CallButton';
import { MobileCallButton } from '../CallButton/MobileCallButton';
import { trackCallButtonClick } from '../CallButton/tracking';
import { OffersLinkContainer } from '../OffersLinkContainer';

export const PromoDetailsModalContainer = () => {
  const promoDetails = useSelector(selectPromoDetails);
  const promoId = useSelector(selectCurrentPromoId);
  const dispatch: TThunkDispatch = useDispatch();

  const preparedPromo = usePreparedPromo();
  const promoModalConfig = usePromoDetailsConfig();

  const closePromoDetailsModal = React.useCallback(() => {
    const path = getLocationPath();
    const hasPromoId = isPathContainsPromoId({ path });

    if (hasPromoId) {
      const cleanPath = removeSearchParam({ path, param: PROMO_ID_QUERYSTRING });
      window.history.pushState({}, '', cleanPath);
    }

    dispatch(closePromoDetails());
  }, [dispatch]);

  const onCallClick = React.useCallback(() => {
    const id = promoDetails?.phoneNewbuildingId;
    const gaLabel = promoDetails?.gaLabel;

    if (!id || !gaLabel) {
      return;
    }

    trackCallButtonClick({
      customUrl: gaLabel,
      id,
      action: 'Open_detail_promo',
      extra: { promoId },
    });
  }, [promoDetails, promoId]);

  const callButtonContext = React.useMemo(() => {
    const phone = promoDetails?.builderPhone;
    if (phone && phone.countryCode) {
      phone.countryCode = phone.countryCode.replace(/\D/g, '');
    }

    const builder = {
      phones: promoDetails?.builderPhone ? [promoDetails.builderPhone] : [],
    };

    return {
      builder,
      siteBlockId: promoDetails?.siteBlockId,
      offerId: promoDetails?.phoneNewbuildingId,
    } as IPromoOffer;
  }, [promoDetails]);

  const handleNewbuildingCountLinkClick = React.useCallback(() => {
    dispatch(trackPromoLinkClickAction(promoId as string, 'to_listingjk'));
  }, [dispatch, promoId]);

  const handleNewbuildingLinkClick = React.useCallback(() => {
    dispatch(trackPromoLinkClickAction(promoId as string, 'to_jk'));
  }, [dispatch, promoId]);

  const handleFullCreditPriceClick = React.useCallback(() => {
    trackCreditFullPriceClick(Boolean(preparedPromo?.isBankMortgage));
  }, [preparedPromo]);

  const handleCianMortgageLinkClick = React.useCallback(() => {
    trackCianMortgageLinkClick(Boolean(preparedPromo?.isBankMortgage));
  }, [preparedPromo]);

  if (!promoId || !preparedPromo || !promoDetails) {
    return null;
  }

  return (
    <PromoModal
      open={true}
      onOpenChange={closePromoDetailsModal}
      promo={preparedPromo}
      controls={
        preparedPromo.cianMortgageButton ? (
          <CianMortgageButton
            url={preparedPromo.cianMortgageButton.url}
            text={preparedPromo.cianMortgageButton.text}
            onClick={handleCianMortgageLinkClick}
          />
        ) : (
          <PromoOfferCardContext.Provider value={callButtonContext}>
            <Responsive phone>
              <MobileCallButton placeType="promoPopup" />
            </Responsive>

            <OffersLinkContainer
              promoId={promoId}
              promoType={promoDetails.promoType}
              newbuildingIds={promoDetails.newbuildingIds}
            />

            <Responsive desktop tablet>
              <CallButton onClick={onCallClick} theme="fill_primary" size="M" placeType="promoPopup" />
            </Responsive>
          </PromoOfferCardContext.Provider>
        )
      }
      config={promoModalConfig}
      onNewbuildingLinkClick={handleNewbuildingLinkClick}
      onNewbuildingListingLinkClick={handleNewbuildingCountLinkClick}
      newbuildingCountText={promoDetails.newbuildingCountText}
      onCreditFullPriceClick={handleFullCreditPriceClick}
    />
  );
};
